import axios from '@/lib/axios'
import { useCefStore } from '@/stores/cef'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import { Cef, Request } from '@/modules/trips/interfaces/cef'

interface Params {
	cefId?: string | number
}

interface Props {
	params: Params
	immediate?: boolean
}

const getCef = async (params: Params): Promise<Request[]> => {
	// errorHandling: false → si el cefId es stale/ajeno y devuelve 403/404, la
	// query falla localmente (isError) en vez de redirigir toda la página vía el
	// interceptor global. Degrada elegante en la pantalla de confirmación.
	const { data } = await axios.get<Request[]>(
		`/api/cefs/${params.cefId}/requests`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)

	return data
}

export const useCefRequests = (props: Props) => {
	const { immediate = true, params } = props || {}
	const cefId = ref(params.cefId || 0)
	const cefstore = useCefStore()

	const { setCef, clearRequests, setRequests } = cefstore
	const {
		cef,
		requestBus,
		requestCar,
		requestHotel,
		requestPlane,
		tabsReferences,
		requests,
	} = storeToRefs(cefstore)

	const ACTIONS: { [key: string]: (req: Request) => void } = {
		Hotel: (req: Request) => {
			requestHotel.value = req
		},
		Plane: (req: Request) => {
			requestPlane.value = req
		},
		Bus: (req: Request) => {
			requestBus.value = req
		},
		Car: (req: Request) => {
			requestCar.value = req
		},
	}

	// Key reactivo: cuando el cefId real se resuelve (p. ej. tras una reserva,
	// arrancando en 0 y actualizándose vía cefId.value), la query vuelve a
	// consultar con el id correcto en vez de quedarse pegada al snapshot inicial.
	// `enabled` exige un cefId válido (>0) para NO disparar `GET /api/cefs/0/requests`,
	// que devuelve 404 y el interceptor global redirige a /error/not-found.
	const isValidCefId = computed(() => immediate && Number(cefId.value) > 0)

	const { data, isError, isLoading, refetch, isSuccess, isFetching } = useQuery(
		['cefsRequests', cefId],
		() =>
			getCef({
				cefId: cefId.value,
			}),
		{
			enabled: isValidCefId,
			retry: false,
			staleTime: 5 * 60 * 1000,
			refetchOnWindowFocus: false,
		}
	)

	const setRequets = (value: number) => {
		cefId.value = value
	}

	watch(
		data,
		(newData) => {
			if (newData && cefId.value) {
				setRequests(newData)
				clearRequests()

				newData.map((req) => {
					ACTIONS[req.serviceName]?.(req)
				})
			}
		},
		{
			immediate: true,
		}
	)

	return {
		cef,
		tabsReferences,
		requestBus,
		requestCar,
		requestHotel,
		requestPlane,
		isError,
		isLoading,
		isSuccess,
		refetch,
		requests,
		isFetching,
		cefId,
	}
}
